import React from "react";
import { dispatchAsync, useRequest } from 'friday-async'
import {
  Drawer, Space, Button, Form, Input, Radio, message,
  Card, Row, Col, Select, Switch, InputNumber,
  Divider, Typography, Tooltip, Upload, Image
} from 'antd'
import { PlusOutlined, DeleteOutlined, InfoCircleOutlined, UploadOutlined, UpOutlined, DownOutlined } from '@ant-design/icons'
import { useApiSelector } from 'src/hooks'
import jsCookie from 'js-cookie'
import config from 'src/config'

const { TextArea } = Input
const { Text } = Typography

// 图片尺寸限制
type ImageSize = { width: number; height: number; label: string }
const validateImageSize = (file: File, expected: ImageSize): Promise<boolean> => {
  return new Promise((resolve) => {
    const img = new window.Image()
    const url = URL.createObjectURL(file)
    img.onload = () => {
      URL.revokeObjectURL(url)
      if (img.width !== expected.width || img.height !== expected.height) {
        message.error(
          `图片尺寸不符：当前 ${img.width}×${img.height}，要求 ${expected.label}（${expected.width}×${expected.height}）`,
          5
        )
        resolve(false)
      } else {
        resolve(true)
      }
    }
    img.onerror = () => {
      URL.revokeObjectURL(url)
      message.error('图片读取失败')
      resolve(false)
    }
    img.src = url
  })
}

// 纯图片上传组件（无 Input 输入框，仅上传按钮 + 预览）
const PureImageUpload: React.FC<{
  value?: string;
  onChange?: (value: string) => void;
  size?: ImageSize;
}> = ({ value, onChange, size }) => {
  const [uploading, setUploading] = React.useState(false)

  const handleUpload = (info: any) => {
    if (info.file.status === 'uploading') {
      setUploading(true)
      return
    }
    if (info.file.status === 'done') {
      setUploading(false)
      const res = info.file.response
      if (res?.data?.url) {
        onChange?.(res.data.url)
        message.success('图片上传成功')
      }
    } else if (info.file.status === 'error') {
      setUploading(false)
      message.error('图片上传失败')
    }
  }

  return (
    <Space direction="vertical" style={{ width: '100%' }}>
      {size && (
        <div style={{ color: '#fa8c16', fontSize: 12 }}>
          📐 推荐尺寸：{size.label}（{size.width}×{size.height}），其他尺寸将被拒绝
        </div>
      )}
      <Upload
        name="image"
        showUploadList={false}
        headers={{
          Authorization: `Bearer ${jsCookie.get('token_jwt') || ''}`
        }}
        action={`${config.host}/api/platform/activity/uploadImage`}
        beforeUpload={size ? (file) => validateImageSize(file, size) : undefined}
        onChange={handleUpload}
      >
        <Button icon={<UploadOutlined />} loading={uploading}>
          {value ? '重新上传' : '上传图片'}
        </Button>
      </Upload>
      {value && (
        <Image
          src={`${config.imgHost}${value.startsWith('/') ? value.slice(1) : value}`}
          alt="预览"
          style={{ maxWidth: 200, maxHeight: 120, objectFit: 'cover', borderRadius: 4, border: '1px solid #d9d9d9' }}
          preview={{ mask: '预览' }}
        />
      )}
    </Space>
  )
}

// 多图上传组件（支持轮播）
const MultipleImageUpload: React.FC<{
  value?: string[];
  onChange?: (value: string[]) => void;
}> = ({ value = [], onChange }) => {
  const [uploading, setUploading] = React.useState(false);

  const handleUpload = (info: any) => {
    if (info.file.status === 'uploading') {
      setUploading(true);
      return;
    }
    if (info.file.status === 'done') {
      setUploading(false);
      const res = info.file.response;
      if (res?.data?.url) {
        const newValue = [...value, res.data.url];
        onChange?.(newValue);
        message.success('图片上传成功');
      }
    } else if (info.file.status === 'error') {
      setUploading(false);
      message.error('图片上传失败');
    }
  };

  const handleRemove = (index: number) => {
    const newValue = value.filter((_, i) => i !== index);
    onChange?.(newValue);
  };

  const handleMoveUp = (index: number) => {
    if (index <= 0) return;
    const newValue = [...value];
    [newValue[index - 1], newValue[index]] = [newValue[index], newValue[index - 1]];
    onChange?.(newValue);
  };

  const handleMoveDown = (index: number) => {
    if (index >= value.length - 1) return;
    const newValue = [...value];
    [newValue[index], newValue[index + 1]] = [newValue[index + 1], newValue[index]];
    onChange?.(newValue);
  };

  return (
    <Space direction="vertical" style={{ width: '100%' }}>
      <Upload
        name="image"
        showUploadList={false}
        headers={{
          Authorization: `Bearer ${jsCookie.get('token_jwt') || ''}`
        }}
        action={`${config.host}/api/platform/activity/uploadImage`}
        onChange={handleUpload}
      >
        <Button icon={<UploadOutlined />} loading={uploading}>
          上传图片
        </Button>
      </Upload>
      {value.length > 0 && (
        <Space wrap>
          {value.map((url, idx) => (
            <div key={idx} style={{ position: 'relative', display: 'inline-block' }}>
              <Image
                src={`${config.imgHost}${url.startsWith('/') ? url.slice(1) : url}`}
                alt={`轮播图 ${idx + 1}`}
                width={100}
                height={60}
                style={{ objectFit: 'cover', borderRadius: 4, border: '1px solid #d9d9d9' }}
                preview={{ mask: '预览' }}
              />
              <div style={{ position: 'absolute', top: -8, right: -8, display: 'flex', gap: 2 }}>
                <Button type="text" size="small" icon={<UpOutlined />}
                  disabled={idx === 0}
                  onClick={() => handleMoveUp(idx)}
                  style={{ color: '#1677FF', padding: 0, width: 18, height: 18, fontSize: 10 }} />
                <Button type="text" size="small" icon={<DownOutlined />}
                  disabled={idx === value.length - 1}
                  onClick={() => handleMoveDown(idx)}
                  style={{ color: '#1677FF', padding: 0, width: 18, height: 18, fontSize: 10 }} />
                <Button type="text" danger size="small" icon={<DeleteOutlined />}
                  onClick={() => handleRemove(idx)}
                  style={{ padding: 0, width: 18, height: 18, fontSize: 10 }} />
              </div>
            </div>
          ))}
        </Space>
      )}
    </Space>
  );
};

// 图文混合上传组件（Input + 上传按钮）

const layout = {
  labelCol: { span: 6 },
  wrapperCol: { span: 18 },
};

interface CreateDrawerProps {
  onClose: () => void
  visible: boolean
  info: null | Record<any, any>
}

// 预设表单字段模板
const PRESET_FIELDS = [
  { key: 'customerName', label: '客户姓名', type: 'text' as const, required: true, placeholder: '请输入姓名' },
  { key: 'phone', label: '手机号', type: 'phone' as const, required: true, placeholder: '请输入手机号' },
  { key: 'carModel', label: '车型', type: 'select' as const, required: true, placeholder: '请选择车型', dictCode: 'car_model' },
  { key: 'province', label: '省份', type: 'select' as const, required: true, dictCode: 'province' },
  { key: 'city', label: '城市', type: 'select' as const, required: true, dictCode: 'city' },
  { key: 'dealer', label: '经销商', type: 'select' as const, required: true, placeholder: '请选择经销商', dictCode: 'dealer' },
]

const CreateDrawer: React.FC<CreateDrawerProps> = (props) => {
  const { onClose, visible, info } = props;
  const [form] = Form.useForm();
  const apis = useApiSelector()
  const isMounted = React.useRef(true)

  const isEdit = !!info

  // 表单字段列表状态
  const [formFields, setFormFields] = React.useState<any[]>([])
  // 权益列表（用于快捷选择）
  const { dataArray: benefitList } = useRequest(apis.benefit.list(), {
    paginated: false,
  })
  // 字典列表（用于引用字典表选项）
  const { dataArray: dictList } = useRequest(apis.dictionary.list(), {
    paginated: false,
  })
  // 短信模板列表
  const { dataArray: smsTemplateList } = useRequest(apis.smsTemplate.list(), {
    paginated: false,
  })

  // 编辑时，权益列表加载后匹配已有权益
  React.useEffect(() => {
    if (isEdit && info?.benefit?.name && benefitList.length > 0) {
      const matched = benefitList.find((b: any) => b.name === info.benefit.name)
      if (matched) {
        form.setFieldsValue({ benefitId: matched._id })
      }
    }
  }, [benefitList, info])

  // 编辑时回填数据
  React.useEffect(() => {
    isMounted.current = true
    if (isEdit && info) {
      form.setFieldsValue({
        name: info.name,
        title: info.title,
        code: info.code,
        description: info.description,
        status: info.status || 'active',
        displayImage: info.displayImage,
        headImage: info.headImage || [],
        subImage: info.subImage || '',
        bgImage: info.bgImage || [],
        protocolUploadEnabled: info.protocolUploadEnabled ?? false,
        smsEnabled: info.smsEnabled ?? false,
        smsTemplateId: info.smsTemplateId ?? undefined,
        ftmsMediaId: info.ftmsConfig?.mediaId ?? '',
        ftmsMediaLeadType: info.ftmsConfig?.mediaLeadType ?? '',
        ftmsChannelKeyId: info.ftmsConfig?.channelKeyId ?? undefined,
        ftmsActivity: info.ftmsConfig?.activity ?? '',
      })
      setFormFields(info.formFields || [])
    } else {
      setFormFields([])
    }
    return () => {
      isMounted.current = false
      form.resetFields()
      setFormFields([])
    }
  }, [info])

  // 添加预设字段
  const addPresetField = (preset: typeof PRESET_FIELDS[0]) => {
    if (formFields.some(f => f.key === preset.key)) {
      message.warning('该字段已添加')
      return
    }
    setFormFields(prev => [...prev, { ...preset, sort: prev.length + 1 }])
  }

  // 添加自定义字段
  const addCustomField = () => {
    const newKey = `custom_${Date.now()}`
    setFormFields(prev => [...prev, {
      key: newKey,
      label: '自定义字段',
      type: 'text',
      required: false,
      placeholder: '',
      options: [],
      sort: prev.length + 1,
    }])
  }

  // 更新字段属性
  const updateField = (index: number, updates: any) => {
    setFormFields(prev => {
      const next = [...prev]
      next[index] = { ...next[index], ...updates }
      return next
    })
  }

  // 删除字段
  const removeField = (index: number) => {
    setFormFields(prev => prev.filter((_, i) => i !== index))
  }

  // 上移/下移
  const moveField = (index: number, direction: 'up' | 'down') => {
    setFormFields(prev => {
      const next = [...prev]
      const target = direction === 'up' ? index - 1 : index + 1
      if (target < 0 || target >= next.length) return prev
      ;[next[index], next[target]] = [next[target], next[index]]
      return next.map((f, i) => ({ ...f, sort: i + 1 }))
    })
  }

  const onFinish = async () => {
    try {
      const basicVals = await form.validateFields()

      // 校验表单字段
      if (formFields.length === 0) {
        return message.warning('请至少添加一个表单字段')
      }

      // 从选中的权益构建 benefit 对象
      let benefitPayload: any = undefined
      if (basicVals.benefitId) {
        const selected = benefitList.find((b: any) => b._id === basicVals.benefitId)
        if (selected) {
          benefitPayload = {
            name: selected.name,
            description: selected.description || '',
            image: selected.image || '',
          }
        }
      }

      const payload = {
        name: basicVals.name,
        title: basicVals.title || '',
        code: basicVals.code,
        description: basicVals.description,
        status: basicVals.status,
        displayImage: basicVals.displayImage || '',
        headImage: basicVals.headImage || '',
        subImage: basicVals.subImage || '',
        bgImage: basicVals.bgImage || [],
        protocolUploadEnabled: basicVals.protocolUploadEnabled || false,
        smsEnabled: basicVals.smsEnabled || false,
        smsTemplateId: basicVals.smsEnabled ? (basicVals.smsTemplateId || '') : '',
        ftmsConfig: (basicVals.ftmsMediaId || basicVals.ftmsMediaLeadType || basicVals.ftmsActivity)
          ? {
              mediaId: basicVals.ftmsMediaId || '',
              mediaLeadType: basicVals.ftmsMediaLeadType || '',
              channelKeyId: basicVals.ftmsChannelKeyId ?? 122,
              activity: basicVals.ftmsActivity || '',
            }
          : undefined,
        formFields: formFields.map((f, idx) => ({
          ...f,
          sort: idx + 1,
          // 清空不用的字段
          options: f.type === 'select' ? (f.options || []) : [],
          fixedValue: f.type === 'fixed' ? (f.fixedValue || '') : '',
          dictCode: f.type === 'select' ? (f.dictCode || '') : '',
        })),
      }
      if (benefitPayload) {
        (payload as any).benefit = benefitPayload
      }

      let error: any
      if (isEdit) {
        const r = await dispatchAsync(apis.activity.update({ _id: info._id, ...payload }))
        error = r.error
      } else {
        const r = await dispatchAsync(apis.activity.create(payload))
        error = r.error
      }

      if (error) return message.error(isEdit ? '编辑失败' : '新建失败')
      message.success(isEdit ? '编辑成功' : '新建成功')
      onClose()
    } catch (err) {
      console.log(err)
    }
  }

  return (
    <Drawer
      title={isEdit ? '编辑活动' : '新建活动'}
      width={700}
      onClose={onClose}
      open={visible}
      destroyOnClose
      styles={{ body: { paddingBottom: 80 } }}
      footer={
        <Space style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button style={{ width: 120 }} onClick={onClose}>取消</Button>
          <Button style={{ width: 120 }} type="primary" onClick={onFinish}>确认</Button>
        </Space>
      }
    >
      <Form {...layout} form={form} scrollToFirstError>
        {/* ====== 基础信息 ====== */}
        <Card title="基本信息" size="small" style={{ marginBottom: 8 }}>
            <Form.Item name="name" label="活动名称" rules={[{ required: true, message: '请输入活动名称' }]}>
              <Input placeholder="如 618购车节" />
            </Form.Item>
            <Form.Item name="title" label="活动标题">
              <Input placeholder="首页展示的标题（可选）" />
            </Form.Item>
            <Form.Item name="code" label="活动标识" rules={[{ required: true, message: '请输入活动标识' }]}>
              <Input placeholder="小程序调用接口的唯一标识" />
            </Form.Item>
            <Form.Item name="description" label="活动描述">
              <TextArea rows={2} placeholder="活动描述（可选）" />
            </Form.Item>
            <Form.Item name="displayImage" label="展示图（首页展示用）">
              <PureImageUpload size={{ width: 680, height: 207, label: '活动展示图 680×207' }} />
            </Form.Item>
            <Form.Item name="headImage" label="头图（轮播）">
              <MultipleImageUpload />
            </Form.Item>
            <Form.Item name="subImage" label="头图下方图片（单图）">
              <PureImageUpload />
            </Form.Item>
            <Form.Item name="bgImage" label="底图（轮播）">
              <MultipleImageUpload />
            </Form.Item>
            <Form.Item name="status" label="活动状态" initialValue={'active'}>
              <Radio.Group>
                <Radio value={'active'}>启用</Radio>
                <Radio value={'inactive'}>下线</Radio>
              </Radio.Group>
            </Form.Item>
        </Card>

        {/* ====== 权益配置 ====== */}
        <Card title="权益配置" size="small" style={{ marginBottom: 8 }}>
            <Form.Item name="benefitId" label="关联权益">
              <Select
                allowClear
                placeholder="请选择权益（非必选）"
                options={benefitList.map((b: any) => ({
                  label: b.name,
                  value: b._id,
                }))}
              />
            </Form.Item>
            {benefitList.length === 0 && (
              <Text type="secondary" style={{ fontSize: 12 }}>暂无权益数据，请先在「权益管理」中添加</Text>
            )}
        </Card>

        {/* ====== 协议配置 ====== */}
        <Card title="协议配置" size="small" style={{ marginBottom: 8 }}>
            <Form.Item name="protocolUploadEnabled" label="开启协议上传" valuePropName="checked" initialValue={false}>
              <Switch />
            </Form.Item>
        </Card>

        {/* ====== 短信配置 ====== */}
        <Card title="短信配置" size="small" style={{ marginBottom: 8 }}>
            <Form.Item name="smsEnabled" label="开启发送短信" valuePropName="checked" initialValue={false}>
              <Switch />
            </Form.Item>
            <Form.Item noStyle shouldUpdate={(prev, cur) => prev.smsEnabled !== cur.smsEnabled}>
              {({ getFieldValue }) => {
                const smsOn = getFieldValue('smsEnabled')
                return smsOn ? (
                  <Form.Item name="smsTemplateId" label="关联短信模板" rules={[{ required: true, message: '请选择短信模板' }]}>
                    <Select
                      allowClear
                      placeholder="请选择短信模板"
                      options={(smsTemplateList || []).map((t: any) => ({
                        label: `${t.name} (${t.templateId})`,
                        value: t.templateId,
                      }))}
                    />
                  </Form.Item>
                ) : null
              }}
            </Form.Item>
            {smsTemplateList?.length === 0 && (
              <Text type="secondary" style={{ fontSize: 12 }}>暂无短信模板，请先在「短信模板管理」中添加</Text>
            )}
        </Card>

        {/* ====== 一丰回传配置 (FTMS) ====== */}
        <Card title="一丰回传配置 (FTMS)" size="small" style={{ marginBottom: 8 }}>
            <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 16 }}>
              用于一丰媒体线索回传，不填写则使用默认配置
            </Text>
            <Form.Item name="ftmsMediaId" label="Media ID" rules={[{ required: true, message: '请输入 Media ID' }]}>
              <Input placeholder="默认: bcba6dcadf7f40c0935ce7d9ba4724f7" />
            </Form.Item>
            <Form.Item name="ftmsMediaLeadType" label="媒体线索类型" rules={[{ required: true, message: '请输入媒体线索类型' }]}>
              <Input placeholder="默认: 小程序活动表单留咨" />
            </Form.Item>
            <Form.Item name="ftmsChannelKeyId" label="渠道 Key ID" rules={[{ required: true, message: '请输入渠道 Key ID' }]}>
              <InputNumber style={{ width: '100%' }} precision={0} placeholder="默认: 122" />
            </Form.Item>
            <Form.Item name="ftmsActivity" label="活动编号" rules={[{ required: true, message: '请输入活动编号' }]}>
              <Input placeholder="默认: 50000004" />
            </Form.Item>
        </Card>

        {/* ====== 表单字段配置 ====== */}
        <Card size="small" title={
            <Space>
              <span>表单字段</span>
              <Text type="secondary" style={{ fontSize: 12 }}>共 {formFields.length} 个字段</Text>
            </Space>
          }>
            {/* 预设字段快速添加 */}
            <div style={{ marginBottom: 12 }}>
              <Text type="secondary" style={{ fontSize: 12 }}>快速添加预设字段：</Text>
              <div style={{ marginTop: 4, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {PRESET_FIELDS.map(pf => {
                  const added = formFields.some(f => f.key === pf.key)
                  return (
                    <Button
                      key={pf.key}
                      size="small"
                      type={added ? 'default' : 'dashed'}
                      disabled={added}
                      onClick={() => addPresetField(pf)}
                      style={{ fontSize: 12 }}
                    >
                      {added ? '✓ ' : '+ '}{pf.label}
                    </Button>
                  )
                })}
              </div>
            </div>

            {/* 已配置的字段列表 */}
            {formFields.length === 0 && (
              <Text type="secondary" style={{ display: 'block', textAlign: 'center', padding: '16px 0' }}>
                请点击上方按钮添加表单字段，或添加自定义字段
              </Text>
            )}

            {formFields.map((field, index) => (
              <Card
                key={field.key}
                size="small"
                style={{ marginBottom: 8 }}
                title={
                  <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                    <Space>
                      <Text strong style={{ fontSize: 13 }}>{field.label}</Text>
                      <Text code style={{ fontSize: 11 }}>{field.key}</Text>
                      {field.required && <Text type="danger" style={{ fontSize: 11 }}>必填</Text>}
                    </Space>
                  </Space>
                }
                extra={
                  <Space size="small">
                    <Button size="small" type="text" disabled={index === 0}
                      onClick={() => moveField(index, 'up')}>↑</Button>
                    <Button size="small" type="text" disabled={index === formFields.length - 1}
                      onClick={() => moveField(index, 'down')}>↓</Button>
                    <Button size="small" type="text" danger icon={<DeleteOutlined />}
                      onClick={() => removeField(index)} />
                  </Space>
                }
              >
                <Row gutter={8}>
                  <Col span={12}>
                    <Form.Item label="标签" style={{ marginBottom: 8 }}>
                      <Input size="small" value={field.label}
                        onChange={e => updateField(index, { label: e.target.value })} />
                    </Form.Item>
                  </Col>
                  <Col span={6}>
                    <Form.Item label="类型" style={{ marginBottom: 8 }}>
                      <Select size="small" value={field.type}
                        onChange={val => updateField(index, { type: val })}
                        options={[
                          { label: '文本', value: 'text' },
                          { label: '手机号', value: 'phone' },
                          { label: '选择', value: 'select' },
                          { label: '文件上传', value: 'upload' },
                          { label: '固定值', value: 'fixed' },
                        ]}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={6}>
                    <Form.Item label="必填" style={{ marginBottom: 8, textAlign: 'center' }}>
                      <Switch size="small" checked={field.required}
                        onChange={val => updateField(index, { required: val })} />
                    </Form.Item>
                  </Col>
                </Row>

                {field.type === 'fixed' && (
                  <Row gutter={8} style={{ marginTop: 8 }}>
                    <Col span={24}>
                      <Form.Item label="固定值" style={{ marginBottom: 0 }}>
                        <Input size="small" value={field.fixedValue || ''}
                          placeholder="提交时自动带上的值"
                          onChange={e => updateField(index, { fixedValue: e.target.value })} />
                      </Form.Item>
                    </Col>
                  </Row>
                )}

                {field.type === 'select' && (
                  <Row gutter={8}>
                    <Col span={12}>
                      <Form.Item label="数据来源" style={{ marginBottom: 0 }}>
                        <Select size="small" value={field.dictCode ? 'dictionary' : 'custom'}
                          onChange={val => {
                            if (val === 'dictionary') {
                              updateField(index, { dictCode: 'province', options: [] })
                            } else {
                              updateField(index, { dictCode: '', options: field.options || [] })
                            }
                          }}
                          options={[
                            { label: '自定义选项', value: 'custom' },
                            { label: '引用字典表', value: 'dictionary' },
                          ]}
                        />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      {field.dictCode ? (
                        <Form.Item label="字典" style={{ marginBottom: 0 }}>
                          <Select size="small" value={field.dictCode}
                            onChange={val => updateField(index, { dictCode: val })}
                            options={(dictList || []).map((d: any) => ({
                              label: `${d.name} (${d.code})`,
                              value: d.code,
                            }))}
                          />
                        </Form.Item>
                      ) : (
                        <Form.Item label="选项" style={{ marginBottom: 0 }}>
                          <Select
                            mode="tags"
                            size="small"
                            value={field.options || []}
                            onChange={vals => updateField(index, { options: vals })}
                            placeholder="输入后回车添加"
                          />
                        </Form.Item>
                      )}
                    </Col>
                  </Row>
                )}
              </Card>
            ))}

            <Button type="dashed" block  onClick={addCustomField} style={{ marginTop: 8 }}>
              添加自定义字段
            </Button>
        </Card>
      </Form>
    </Drawer>
  )
}

export default CreateDrawer
